import { CheckCircle, FeaturesList, SSRModalDropDownSelection } from '@fz/ui-components-react';
import { SsrType } from '@models/common/shared-enums.model';
import type { MealAndAssistData } from '@models/landing/api/baggage.model';
import type { Passenger } from '@models/api/extras-response.model';
import {
  type Dispatch,
  type ReactElement,
  type SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { IconClose } from '@shared/icons';
import { getResource } from '@utils/resource-translator.util';
import { PASSENGER_NAVIGATION_DELAY } from '@utils/passenger-navigation.util';
import {
  mapAssistToModalView,
  buildGroupsForFlightId,
  resolveAssistPassengerSlotLabel,
  type AssistAddApiPayload,
  type AssistModalOptionInfo,
} from '@shared/components/MeetAndAssistSsrModalComponent/meet-and-assist.view.mapper';
import {
  countNonInfantPassengers,
  findNextSelectablePassengerId,
  isInfantPassengerTypeLabel,
} from '@shared/components/ssr-passenger.util';
import './_meet-and-assist-ssr-modal.scss';

// ============================================================================
// TYPES
// ============================================================================

interface MeetAndAssistProps {
  mealAndAssist?: MealAndAssistData;
  passengerList?: Passenger[];
  onConfirmSelection?: (payloads: AssistAddApiPayload[]) => void;
  onSelectionAmountChange?: (amount: number) => void;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}

/** keyed by logicalFlightId → resPaxId string → selected optionId */
type AssistTabPassengerSelectionMap = Record<string, Record<string, string>>;

const FEATURE_KEYS = [
  {
    key: 'Extra.Assist.Boardingpass.Text',
    fallback: 'Extra.Assist.Boardingpass.Text',
  },
  {
    key: 'Extra.Assist.Support.Text',
    fallback: 'Extra.Assist.Support.Text',
  },
  {
    key: 'Extra.Assist.Luggage.Text',
    fallback: 'Extra.Assist.Luggage.Text',
  },
  {
    key: 'Extra.Assist.Priority.Text',
    fallback: 'Extra.Assist.Priority.Text',
  },
] as const;
const ASSIST_SSR_TYPE = String(SsrType.ASSIST);
const ALL_PASSENGERS_ID = 'all-passengers';
const SSR_CLOSE_ICON = <IconClose size={27} aria-hidden={true} />;

const resolveToggleStateForContext = (
  passengerList: Passenger[] | undefined,
  selectedPassengerId: string,
  activeFlightTabValue: string,
): 'YES' | 'NO' => {
  if (!passengerList || passengerList.length === 0) {
    return 'YES';
  }

  const passenger = passengerList.find((p) => p.resPaxId.toString() === selectedPassengerId);
  if (!passenger) {
    return 'YES';
  }

  const flightDetail = passenger.flightsDetail?.find(
    (fd) => fd.logicalFlightId === activeFlightTabValue,
  );
  const includedAssist = flightDetail?.includedExtrasList?.find(
    (extra) => String(extra.ssrType) === ASSIST_SSR_TYPE,
  );

  if (!includedAssist) {
    return 'YES';
  }

  return includedAssist.isAssistOpted ? 'YES' : 'NO';
};

// ============================================================================
// SELECTION MAP HELPERS
// ============================================================================

// eslint-disable-next-line max-lines-per-function
const buildSelectionMap = (
  mealAndAssist?: MealAndAssistData,
  passengerList?: Passenger[],
  optionInfoByFlightId?: Record<string, AssistModalOptionInfo[]>,
): AssistTabPassengerSelectionMap => {
  if (!mealAndAssist) return {};

  const uniqueTabValues = mealAndAssist.assistQuotes
    .filter(
      (quote, index, quotes) =>
        quotes.findIndex((q) => q.logicalFlightId === quote.logicalFlightId) === index,
    )
    .map((quote) => quote.logicalFlightId);

  const paxList = passengerList ?? [];

  return uniqueTabValues.reduce<AssistTabPassengerSelectionMap>((tabAcc, tabValue) => {
    // eslint-disable-next-line complexity
    tabAcc[tabValue] = paxList.reduce<Record<string, string>>((paxAcc, passenger) => {
      const availableOptions = optionInfoByFlightId?.[tabValue] ?? [];

      // Check if passenger has included ASSIST with subCodeType for this flight
      // Only pre-select if subCodeType is present, otherwise show placeholder
      const flightDetail = passenger.flightsDetail?.find((fd) => fd.logicalFlightId === tabValue);
      const includedAssist = flightDetail?.includedExtrasList?.find(
        (extra) => String(extra.ssrType) === ASSIST_SSR_TYPE && extra.subCodeType,
      );

      if (includedAssist) {
        const matchingOption = availableOptions.find(
          (opt) => opt.subCodeType === includedAssist.subCodeType,
        );
        paxAcc[passenger.resPaxId.toString()] = matchingOption?.id ?? '';

        return paxAcc;
      }

      // Fallback to selected SSR list when includedExtrasList is not yet updated.
      // eslint-disable-next-line complexity
      const selectedAssistSsr = (mealAndAssist.selectedSSRList ?? []).find((ssr) => {
        const typedSsr = ssr as Record<string, unknown>;
        const ssrType = String(typedSsr.ssrType ?? '').toUpperCase();
        if (ssrType !== ASSIST_SSR_TYPE) {
          return false;
        }

        const ssrResPaxId = Number(typedSsr.resPaxId ?? typedSsr.resPaxID ?? Number.NaN);
        if (ssrResPaxId !== passenger.resPaxId) {
          return false;
        }

        const logicalFlightId = String(typedSsr.logicalFlightId ?? typedSsr.logicalFlightID ?? '');
        const physicalFlightId = String(
          typedSsr.physicalFlightId ?? typedSsr.physicalFlightID ?? '',
        );

        if (logicalFlightId === tabValue) {
          return true;
        }

        return availableOptions.some((option) => option.physicalFlightId === physicalFlightId);
      }) as Record<string, unknown> | undefined;

      if (!selectedAssistSsr) {
        paxAcc[passenger.resPaxId.toString()] = '';
        return paxAcc;
      }

      const isAssistOpted =
        selectedAssistSsr.isAssistOpted === undefined
          ? true
          : Boolean(selectedAssistSsr.isAssistOpted);

      if (!isAssistOpted) {
        paxAcc[passenger.resPaxId.toString()] = '';
        return paxAcc;
      }

      const selectedSubCodeType = String(selectedAssistSsr.subCodeType ?? '');
      const selectedCodeType = String(selectedAssistSsr.codeType ?? '');
      const matchingOptionFromSelectedSsr = selectedSubCodeType
        ? availableOptions.find((option) => option.subCodeType === selectedSubCodeType)
        : availableOptions.find((option) => option.codeType === selectedCodeType);

      paxAcc[passenger.resPaxId.toString()] = matchingOptionFromSelectedSsr?.id ?? '';

      return paxAcc;
    }, {});
    return tabAcc;
  }, {});
};

// ============================================================================
// PASSENGER SSR DETAILS HELPER
// ============================================================================

const resolvePassengerSsrDetails = (
  baseDetails: ReturnType<typeof mapAssistToModalView>['passengerSSRDetails'],
  selectionByPassenger: Record<string, string>,
  optionInfoList: AssistModalOptionInfo[],
): Record<string, { subText1?: string; subText2?: string }> => {
  const details = { ...(baseDetails ?? {}) };

  Object.entries(selectionByPassenger).forEach(([passengerId, selectedOptionId]) => {
    const currentDetails = details[passengerId] ?? { subText1: '', subText2: '' };

    if (!selectedOptionId) {
      details[passengerId] = {
        subText1: currentDetails.subText1 ?? '',
        subText2: '',
      };
      return;
    }

    const selectedOption = optionInfoList.find((info) => info.id === selectedOptionId);
    const resolvedSlotLabel = selectedOption ? resolveAssistPassengerSlotLabel(selectedOption) : '';

    details[passengerId] = {
      subText1: currentDetails.subText1 ?? '',
      subText2: resolvedSlotLabel ? resolvedSlotLabel : '',
    };
  });

  return details;
};

// ============================================================================
// PAYLOAD BUILDER
// ============================================================================

const buildPayloadsForConfirm = (
  selectionByTabPassenger: AssistTabPassengerSelectionMap,
  optionInfoByFlightId: Record<string, AssistModalOptionInfo[]>,
  toggleState: 'YES' | 'NO',
): AssistAddApiPayload[] => {
  const isAssistOpted = toggleState === 'YES';

  return Object.entries(selectionByTabPassenger).flatMap(([tabValue, selectionByPassenger]) => {
    const optionInfoList = optionInfoByFlightId[tabValue] ?? [];

    return Object.entries(selectionByPassenger).flatMap(([passengerId, optionId]) => {
      const selectedOption = optionInfoList.find((info) => info.id === optionId);
      const fallbackOption = optionInfoList.find((info) =>
        info.passengerDetails.some((detail) => detail.resPaxId === Number(passengerId)),
      );
      const optionInfo = selectedOption ?? fallbackOption;
      if (!optionInfo) return [];

      if (isAssistOpted && !selectedOption) {
        return [];
      }

      const passengerDetail = optionInfo.passengerDetails.find(
        (detail) => detail.resPaxId === Number(passengerId),
      );
      if (!passengerDetail) return [];

      return [
        {
          ssrType: SsrType.ASSIST,
          logicalFlightId: optionInfo.logicalFlightId,
          physicalFlightId: optionInfo.physicalFlightId,
          paxJourneyId: passengerDetail.paxJourneyId,
          resPaxId: passengerDetail.resPaxId,
          recordNumber: passengerDetail.recordNumber,
          codeType: optionInfo.codeType,
          subCodeType: isAssistOpted ? optionInfo.subCodeType : '',
          isBusiness: optionInfo.isBusiness,
          secureHash: optionInfo.secureHash,
          assistSlotFrom: isAssistOpted ? optionInfo.slotFrom : '',
          assistSlotTo: isAssistOpted ? optionInfo.slotTo : '',
          isAssistOpted,
        },
      ];
    });
  });
};

const isInfantPassengerId = (
  passengers: ReturnType<typeof mapAssistToModalView>['passengers'],
  passengerId: string,
): boolean => {
  const passenger = passengers.find((item) => item.id === passengerId);
  return isInfantPassengerTypeLabel(passenger?.type);
};

// ============================================================================
// COMPONENT
// ============================================================================

// eslint-disable-next-line max-lines-per-function
export const MeetAndAssistComponent = ({
  mealAndAssist,
  passengerList,
  onConfirmSelection,
  onSelectionAmountChange,
  isOpen,
  setIsOpen,
}: MeetAndAssistProps): ReactElement => {
  const resolveResourceText = (key: string, fallback: string = key): string => {
    const translated = getResource(key);
    return translated && translated !== key ? translated : fallback;
  };

  const assistView = mapAssistToModalView(mealAndAssist, passengerList);

  const [selectedPassenger, setSelectedPassenger] = useState(assistView.initialSelectedPassengerId);
  const [activeFlightTab, setActiveFlightTab] = useState(assistView.initialActiveFlightTabValue);
  const [toggleState, setToggleState] = useState<'YES' | 'NO'>(() =>
    resolveToggleStateForContext(
      passengerList,
      assistView.initialSelectedPassengerId,
      assistView.initialActiveFlightTabValue,
    ),
  );
  const [defaultSelectionByTabPassenger, setDefaultSelectionByTabPassenger] =
    useState<AssistTabPassengerSelectionMap>(() =>
      buildSelectionMap(mealAndAssist, passengerList, assistView.optionInfoByFlightId),
    );
  const [selectionByTabPassenger, setSelectionByTabPassenger] =
    useState<AssistTabPassengerSelectionMap>(() =>
      buildSelectionMap(mealAndAssist, passengerList, assistView.optionInfoByFlightId),
    );
  const [confirmedSelectionByTabPassenger, setConfirmedSelectionByTabPassenger] =
    useState<AssistTabPassengerSelectionMap>(() =>
      buildSelectionMap(mealAndAssist, passengerList, assistView.optionInfoByFlightId),
    );
  const [confirmedToggleState, setConfirmedToggleState] = useState<'YES' | 'NO'>(() =>
    resolveToggleStateForContext(
      passengerList,
      assistView.initialSelectedPassengerId,
      assistView.initialActiveFlightTabValue,
    ),
  );
  const [isSameForAll, setIsSameForAll] = useState(false);

  useEffect(() => {
    const initialSelectionMap = buildSelectionMap(
      mealAndAssist,
      passengerList,
      assistView.optionInfoByFlightId,
    );
    const initialToggleState = resolveToggleStateForContext(
      passengerList,
      assistView.initialSelectedPassengerId,
      assistView.initialActiveFlightTabValue,
    );

    setSelectedPassenger(assistView.initialSelectedPassengerId);
    setActiveFlightTab(assistView.initialActiveFlightTabValue);
    setToggleState(initialToggleState);
    setDefaultSelectionByTabPassenger(initialSelectionMap);
    setSelectionByTabPassenger(initialSelectionMap);
    setConfirmedSelectionByTabPassenger(initialSelectionMap);
    setConfirmedToggleState(initialToggleState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mealAndAssist,
    passengerList,
    assistView.initialSelectedPassengerId,
    assistView.initialActiveFlightTabValue,
  ]);

  useEffect(() => {
    setToggleState(resolveToggleStateForContext(passengerList, selectedPassenger, activeFlightTab));
  }, [passengerList, selectedPassenger, activeFlightTab]);

  useEffect(() => {
    if (!isOpen) {
      setIsSameForAll(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isSameForAll) {
      return;
    }

    // First try to get the current passenger's selection
    let selectedOptionIdForAllPassengers =
      selectionByTabPassenger[activeFlightTab]?.[selectedPassenger] ?? '';

    // If current passenger has no selection, find the first non-infant passenger with a selection
    if (!selectedOptionIdForAllPassengers) {
      const activeTabSelections = selectionByTabPassenger[activeFlightTab] ?? {};
      selectedOptionIdForAllPassengers =
        Object.entries(activeTabSelections).find(
          ([passengerId, optionId]) =>
            !isInfantPassengerId(assistView.passengers, passengerId) && Boolean(optionId),
        )?.[1] ?? '';
    }

    // Only apply if we found an actual selection
    if (!selectedOptionIdForAllPassengers) {
      return;
    }

    setSelectionByTabPassenger((previous) => {
      const activeTabSelections = previous[activeFlightTab] ?? {};
      const nextTabSelections = Object.keys(activeTabSelections).reduce<Record<string, string>>(
        (accumulator, passengerId) => {
          if (isInfantPassengerId(assistView.passengers, passengerId)) {
            accumulator[passengerId] = activeTabSelections[passengerId] ?? '';
            return accumulator;
          }

          accumulator[passengerId] = selectedOptionIdForAllPassengers;
          return accumulator;
        },
        {},
      );

      const hasDifference = Object.entries(activeTabSelections).some(
        ([passengerId, optionId]) => optionId !== nextTabSelections[passengerId],
      );

      if (!hasDifference) {
        return previous;
      }

      return {
        ...previous,
        [activeFlightTab]: nextTabSelections,
      };
    });
  }, [
    isSameForAll,
    selectionByTabPassenger,
    activeFlightTab,
    selectedPassenger,
    assistView.passengers,
  ]);

  const showSameForAllPassengers = countNonInfantPassengers(assistView.passengers) > 1;

  const allPassengersCard = [
    {
      id: ALL_PASSENGERS_ID,
      name: `${resolveResourceText('Extras.AllPassengers.Text', 'All passengers')} (${countNonInfantPassengers(assistView.passengers)})`,
      type: '',
      seatNumber: '',
      pnrNumber: '',
    },
  ];

  const infantPassengers = assistView.passengers.filter((passenger) => {
    return isInfantPassengerTypeLabel(passenger.type);
  });

  const passengersForModal = isSameForAll
    ? [...allPassengersCard, ...infantPassengers]
    : assistView.passengers;

  const selectedPassengerIdForModal = isSameForAll ? ALL_PASSENGERS_ID : selectedPassenger;

  const optionInfoForActiveTab = useMemo(
    () => assistView.optionInfoByFlightId[activeFlightTab] ?? [],
    [assistView.optionInfoByFlightId, activeFlightTab],
  );

  const groupsForActiveTab = useMemo(
    () =>
      buildGroupsForFlightId(
        assistView.optionInfoByFlightId,
        activeFlightTab,
        isSameForAll
          ? Object.entries(selectionByTabPassenger[activeFlightTab] ?? {}).find(
              ([passengerId, optionId]) =>
                !isInfantPassengerId(assistView.passengers, passengerId) && Boolean(optionId),
            )?.[1]
          : selectionByTabPassenger[activeFlightTab]?.[selectedPassenger],
      ),
    [
      assistView.optionInfoByFlightId,
      activeFlightTab,
      selectionByTabPassenger,
      selectedPassenger,
      isSameForAll,
      assistView.passengers,
    ],
  );

  const selectedPrice = useMemo(() => {
    const selectionByPassenger = selectionByTabPassenger[activeFlightTab] ?? {};
    return Object.values(selectionByPassenger).reduce<number>((total, optionId) => {
      if (!optionId) return total;
      const info = optionInfoForActiveTab.find((o) => o.id === optionId);
      return total + (info ? Number(info.amount) : 0);
    }, 0);
  }, [selectionByTabPassenger, activeFlightTab, optionInfoForActiveTab]);

  const priceAmountForFooter = selectedPrice > 0 ? selectedPrice.toString() : '';
  const currencyCodeForFooter = assistView.currencyCode;

  const hasAnySelection = Object.values(selectionByTabPassenger).some((selectionByPassenger) =>
    Object.values(selectionByPassenger).some((selectedOptionId) => Boolean(selectedOptionId)),
  );

  const passengerSsrDetails = useMemo(
    () =>
      resolvePassengerSsrDetails(
        assistView.passengerSSRDetails,
        selectionByTabPassenger[activeFlightTab] ?? {},
        optionInfoForActiveTab,
      ),
    [
      assistView.passengerSSRDetails,
      selectionByTabPassenger,
      activeFlightTab,
      optionInfoForActiveTab,
    ],
  );

  const passengerSsrDetailsForModal = useMemo(() => {
    if (!isSameForAll) {
      return passengerSsrDetails;
    }

    const selectedOptionForAllPassengers = Object.entries(
      selectionByTabPassenger[activeFlightTab] ?? {},
    ).find(([passengerId, optionId]) => {
      return !isInfantPassengerId(assistView.passengers, passengerId) && Boolean(optionId);
    })?.[1];

    const selectedOptionInfo = optionInfoForActiveTab.find(
      (option) => option.id === selectedOptionForAllPassengers,
    );

    return {
      [ALL_PASSENGERS_ID]: {
        subText1: '',
        subText2: selectedOptionInfo ? resolveAssistPassengerSlotLabel(selectedOptionInfo) : '',
      },
      ...Object.fromEntries(
        infantPassengers.map((passenger) => {
          return [
            passenger.id,
            passengerSsrDetails[passenger.id] ?? {
              subText1: '',
              subText2: '',
            },
          ];
        }),
      ),
    };
  }, [
    isSameForAll,
    passengerSsrDetails,
    selectionByTabPassenger,
    activeFlightTab,
    assistView.passengers,
    optionInfoForActiveTab,
    infantPassengers,
  ]);

  const featuresListContent = useMemo((): ReactElement => {
    return (
      <FeaturesList
        features={FEATURE_KEYS.map((item) => ({
          id: item.key,
          icon: <CheckCircle size={16} />,
          text: resolveResourceText(item.key, item.fallback),
          labelClassName: 'label7',
        }))}
      />
    );
  }, []);

  const assistToggleLabels = {
    isAssistOptedYesLabel: resolveResourceText('Yes.Text', 'YES'),
    isAssistOptedNoLabel: resolveResourceText('No.Text', 'NO'),
  } as Record<string, string>;
  const passengersTitle = resolveResourceText('SelectPassenger.Passengers.Text', 'Passengers');

  const handleToggle = (): void => {
    setToggleState((previous) => {
      const nextToggleState = previous === 'YES' ? 'NO' : 'YES';

      if (nextToggleState === 'NO') {
        setSelectionByTabPassenger((previousSelections) => {
          const activeTabSelections = previousSelections[activeFlightTab] ?? {};

          if (isSameForAll) {
            const clearedSelections = Object.keys(activeTabSelections).reduce<
              Record<string, string>
            >((accumulator, passengerId) => {
              if (isInfantPassengerId(assistView.passengers, passengerId)) {
                accumulator[passengerId] = activeTabSelections[passengerId] ?? '';
                return accumulator;
              }

              accumulator[passengerId] = '';
              return accumulator;
            }, {});

            return {
              ...previousSelections,
              [activeFlightTab]: clearedSelections,
            };
          }

          return {
            ...previousSelections,
            [activeFlightTab]: {
              ...activeTabSelections,
              [selectedPassenger]: '',
            },
          };
        });
      }

      return nextToggleState;
    });
  };

  const handleSelectOption = (_groupId: string, optionId: string): void => {
    const currentValue = selectionByTabPassenger[activeFlightTab]?.[selectedPassenger] ?? '';
    const nextValue = currentValue === optionId ? '' : optionId;

    setSelectionByTabPassenger((previous) => {
      if (isSameForAll) {
        const activeTabSelections = previous[activeFlightTab] ?? {};
        const nextTabSelections = Object.keys(activeTabSelections).reduce<Record<string, string>>(
          (accumulator, passengerId) => {
            if (isInfantPassengerId(assistView.passengers, passengerId)) {
              accumulator[passengerId] = activeTabSelections[passengerId] ?? '';
              return accumulator;
            }

            accumulator[passengerId] = nextValue;
            return accumulator;
          },
          {},
        );

        return {
          ...previous,
          [activeFlightTab]: nextTabSelections,
        };
      }

      return {
        ...previous,
        [activeFlightTab]: {
          ...(previous[activeFlightTab] ?? {}),
          [selectedPassenger]: nextValue,
        },
      };
    });

    if (nextValue === '' || isSameForAll) return;

    const nextPassengerId: string | undefined = findNextSelectablePassengerId(
      assistView.passengers,
      selectedPassenger,
    );

    if (nextPassengerId) {
      setTimeout(() => {
        setSelectedPassenger(nextPassengerId);
      }, PASSENGER_NAVIGATION_DELAY);
    }
  };

  const resolveTotalSelectedAmount = (): number => {
    if (toggleState !== 'YES') {
      return 0;
    }

    return Object.entries(selectionByTabPassenger).reduce<number>(
      (tabsAcc, [tabValue, selectionByPassenger]) => {
        const optionInfoForTab = assistView.optionInfoByFlightId[tabValue] ?? [];
        const optionAmountById = optionInfoForTab.reduce<Record<string, number>>(
          (acc, optionInfo) => {
            const parsedAmount = Number(optionInfo.amount);
            acc[optionInfo.id] = Number.isFinite(parsedAmount) ? parsedAmount : 0;
            return acc;
          },
          {},
        );

        const tabAmount = Object.values(selectionByPassenger).reduce<number>(
          (passengerAcc, optionId) => {
            if (!optionId) {
              return passengerAcc;
            }

            return passengerAcc + (optionAmountById[optionId] ?? 0);
          },
          0,
        );

        return tabsAcc + tabAmount;
      },
      0,
    );
  };

  const handleConfirm = (): void => {
    onSelectionAmountChange?.(resolveTotalSelectedAmount());

    const payloads = buildPayloadsForConfirm(
      selectionByTabPassenger,
      assistView.optionInfoByFlightId,
      toggleState,
    );

    setConfirmedSelectionByTabPassenger(selectionByTabPassenger);
    setConfirmedToggleState(toggleState);

    if (payloads.length > 0 && onConfirmSelection) {
      onConfirmSelection(payloads);
    }

    setIsOpen(false);
  };

  const resetTransientSelectionState = (): void => {
    setSelectionByTabPassenger(confirmedSelectionByTabPassenger);
    setToggleState(confirmedToggleState);
    setIsSameForAll(false);
    setSelectedPassenger(assistView.initialSelectedPassengerId);
    setActiveFlightTab(assistView.initialActiveFlightTabValue);
  };

  const handleModalClose = (): void => {
    resetTransientSelectionState();
    setIsOpen(false);
  };

  const handleClearSelections = (): void => {
    setSelectionByTabPassenger((previous) => {
      const activeTabSelections = previous[activeFlightTab] ?? {};
      const defaultTabSelections = defaultSelectionByTabPassenger[activeFlightTab] ?? {};
      const nextTabSelections = Object.keys(activeTabSelections).reduce<Record<string, string>>(
        (accumulator, passengerId) => {
          accumulator[passengerId] = defaultTabSelections[passengerId] ?? '';
          return accumulator;
        },
        {},
      );

      return {
        ...previous,
        [activeFlightTab]: nextTabSelections,
      };
    });
    setToggleState(resolveToggleStateForContext(passengerList, selectedPassenger, activeFlightTab));
    setIsSameForAll(false);
  };

  const handleClearClick = (): void => {
    handleClearSelections();
  };

  return (
    <SSRModalDropDownSelection
      isOpen={isOpen}
      onClear={handleClearClick}
      title={assistView.title}
      closeIcon={SSR_CLOSE_ICON}
      passengersTitle={passengersTitle}
      passengers={passengersForModal}
      selectedPassengerId={selectedPassengerIdForModal}
      onPassengerSelect={(passengerId) => {
        if (passengerId === ALL_PASSENGERS_ID) {
          return;
        }

        if (isInfantPassengerId(assistView.passengers, passengerId)) {
          return;
        }

        setSelectedPassenger(passengerId);
      }}
      onClose={handleModalClose}
      onConfirm={handleConfirm}
      priceAmount={priceAmountForFooter}
      confirmButtonLabel={assistView.confirmButtonLabel}
      currencyCode={currencyCodeForFooter}
      passengerSSRDetails={passengerSsrDetailsForModal}
      flightTabItems={assistView.flightTabItems}
      activeFlightTabValue={activeFlightTab}
      onFlightTabChange={setActiveFlightTab}
      contentTitle={assistView.contentTitle}
      contentDescription={assistView.contentDescription}
      topContent={featuresListContent}
      infoPanelText={assistView.infoPanelText}
      toggleState={toggleState}
      {...assistToggleLabels}
      onToggle={handleToggle}
      groups={groupsForActiveTab}
      onSelectOption={handleSelectOption}
      passengerTypeLabelProps={{ className: 'label5', variant: 'tertiary' }}
      isUnavailable={assistView.isUnavailable}
      unavailableTitle={assistView.unavailableTitle}
      unavailableDescription={assistView.unavailableDescription}
      unavailableVariant={assistView.unavailableVariant}
      disableInfants={true}
      showClearButton={hasAnySelection}
      clearLabel={resolveResourceText('Clear.Text')}
      showSameForAllPassengers={showSameForAllPassengers}
      sameForAllPassengersLabel={resolveResourceText('Extras.SameForAllPassengers.Text')}
      onSameForAllPassengersChange={(checked) => {
        setIsSameForAll(checked === true);
      }}
    />
  );
};
